import { i18n } from "~vue/i18n";
import type { ComputedRef, Ref } from "vue";
import { computed, ref } from "vue";

export const ALL_FILTER_OPTION = "__all__";

export interface ConnectionFacet {
    id: string;
    name: string;
    values: string[];
}

export interface FacetOption {
    key: string;
    label: string;
}

export interface ActiveFacetFilterEntry {
    id: string;
    name: string;
    value: string;
}

export type FacetFilters = Record<string, string>;

export const useConnectionFacetFilters = (facets: ComputedRef<ConnectionFacet[]>, onFiltersChanged: () => void) => {
    const activeFacetFilters: Ref<FacetFilters> = ref({});

    const meaningfulFacets = computed(() => facets.value.filter((facet) => facet.values.length > 1));
    const meaningfulFacetIdSet = computed(() => new Set(meaningfulFacets.value.map((facet) => facet.id)));
    const showSingleFacetControl = computed(() => meaningfulFacets.value.length === 1);
    const singleFacet = computed<ConnectionFacet | null>(() => meaningfulFacets.value[0] ?? null);

    const normalizeActiveFacetFilters = (filters: FacetFilters): FacetFilters =>
        Object.fromEntries(Object.entries(filters).filter(([facetId, value]) => value && meaningfulFacetIdSet.value.has(facetId)));

    const normalizedFacetFilters = computed(() => normalizeActiveFacetFilters(activeFacetFilters.value));
    const hasActiveFacetFilters = computed(() => Object.keys(normalizedFacetFilters.value).length > 0);
    const activeFacetFilterEntries = computed<ActiveFacetFilterEntry[]>(() =>
        meaningfulFacets.value
            .filter((facet) => normalizedFacetFilters.value[facet.id])
            .map((facet) => ({
                id: facet.id,
                name: facet.name,
                value: normalizedFacetFilters.value[facet.id],
            })),
    );

    const singleFacetValue = computed(() => {
        if (!singleFacet.value) {
            return ALL_FILTER_OPTION;
        }

        return activeFacetFilters.value[singleFacet.value.id] || ALL_FILTER_OPTION;
    });

    const singleFacetOptions = computed<FacetOption[]>(() => {
        if (!singleFacet.value) {
            return [];
        }

        return [
            { key: ALL_FILTER_OPTION, label: i18n.global.t("connections.facetFilter.allFacetValues", { facet: singleFacet.value.name }) },
            ...singleFacet.value.values.map((value) => ({ key: value, label: value })),
        ];
    });

    const handleSingleFacetChange = (value: string) => {
        if (!singleFacet.value) {
            return;
        }

        activeFacetFilters.value = value && value !== ALL_FILTER_OPTION ? { [singleFacet.value.id]: value } : {};
        onFiltersChanged();
    };

    const handleFacetFiltersChange = (filters: FacetFilters) => {
        activeFacetFilters.value = normalizeActiveFacetFilters(filters);
        onFiltersChanged();
    };

    const removeFacetFilter = (facetId: string) => {
        const nextFilters = { ...activeFacetFilters.value };
        delete nextFilters[facetId];
        activeFacetFilters.value = nextFilters;
        onFiltersChanged();
    };

    const clearFacetFilters = () => {
        activeFacetFilters.value = {};
        onFiltersChanged();
    };

    return {
        activeFacetFilters,
        activeFacetFilterEntries,
        hasActiveFacetFilters,
        meaningfulFacets,
        normalizeActiveFacetFilters,
        normalizedFacetFilters,
        showSingleFacetControl,
        singleFacet,
        singleFacetOptions,
        singleFacetValue,
        handleSingleFacetChange,
        handleFacetFiltersChange,
        removeFacetFilter,
        clearFacetFilters,
    };
};
